import { z } from "zod";
import type { MediaOrigin } from "./api.js";
import { cleanDomain } from "./site.js";

/**
 * The pictures, logo and colours a lookup gathers, and the owner's choice of what to keep (ADR 0029, 03 §your look).
 *
 * The old client kept whatever it found and asked nothing, or asked on a screen that could leave somebody stuck. Here
 * everything found is a **candidate** until the owner chooses: shown beside the conversation, grouped by where it came
 * from, with the likely logo and colours already picked. Nothing reaches the media library, the logo or the palette
 * until they say so, and what they leave behind is deleted.
 */

/** Where a candidate was found. Each keeps its own mark in the media library once kept. */
export type BrandSource = "website" | "google" | "web";

export const BRAND_ORIGIN: Record<BrandSource, MediaOrigin> = { website: "website", google: "google", web: "web" };

/** The groups the picker shows, in order: the owner's own site first, because it is theirs beyond doubt. */
export const BRAND_SOURCES: ReadonlyArray<[BrandSource, string]> = [
  ["website", "From your website"],
  ["google", "From your Google listing"],
  ["web", "Found online"],
];

export interface BrandCandidate {
  id: string;
  source: BrandSource;
  /** Where it was found, in the words under it: the site's host, "Google Maps", "facebook.com". */
  from: string;
  /** The page it was found on, when there is one worth opening. */
  link: string | null;
  width: number;
  height: number;
  /** It looks like a logo: named so, in the site's header, or the icon the site gives phones. */
  logo: boolean;
  /** Its own colours, most used first. Worked out for logo candidates only. */
  colours: string[];
}

/** A candidate as it is kept on `business_profiles.brand`: the screen's view, and where its bytes are. */
export interface BrandStored extends BrandCandidate {
  key: string;
  mime: string;
  bytes: number;
  sha256: string | null;
  thumbKey: string;
}

/** `business_profiles.brand`: one gathering, waiting for a choice. */
export interface BrandKit {
  candidates: BrandStored[];
  /** Colours the website itself declares, most likely first. */
  colours: string[];
  at: string;
}

/** What the screen is given to choose from. */
export interface BrandView {
  candidates: BrandCandidate[];
  /** The colours to start from: the likeliest logo's, then the site's. */
  colours: string[];
  /** The colours the website declares, on their own, for proposing themes whichever logo is picked. */
  siteColours: string[];
  /** The candidate most likely to be the logo, chosen already so a quick yes keeps it. */
  logo: string | null;
}

/** The logo and colours as they stand. `logoUrl` is this API's own route. */
export interface LookView {
  logoUrl: string | null;
  palette: string[];
  /** The colours the logo is drawn in and the colours the website declares, kept to propose themes from. */
  logoColours: string[];
  siteColours: string[];
  /** Under the name on the last frame of a video whose brief has no line of its own, as the render draws it (`endLine`). */
  endLine: string;
}

/**
 * The second line of a video's last frame, under the name (docs/05, the brand's close): one next step for whoever has
 * just watched. The brief's own line first (`said`, already cut to fit), since the owner or the week plan wrote it to
 * be on screen: an offer, a phone number, opening hours. Else the business's one website address (ADR 0040,
 * `workspaces.site_domain`), else the host of the website the lookup found, else its phone, else nothing. Until
 * 2026-10-12 it was the phone alone, and blank without one. The Look panel's preview asks with no brief, and gets the
 * line a video with none ends on.
 */
export function endLine(said: string | null | undefined, business: { siteDomain?: string | null | undefined; websiteUrl?: string | null | undefined; phone?: string | null | undefined }): string {
  const site = business.siteDomain || (business.websiteUrl ? cleanDomain(business.websiteUrl) : null);
  return said?.trim() || site || business.phone?.trim() || "";
}

/** `business_profiles.look_colours`: what themes are proposed from, once the lookup that found them is over. */
export interface LookColours {
  logo: string[];
  site: string[];
}

/* ── colour themes ─────────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * A theme is two of the brand's colours, as they are, or three when a logo has two colours and the dark it is drawn
 * in. The renderer reads a palette by role, not by order (`brandRoles`, ADR 0060): the darkest is the dark ground and
 * the words on the light, the lightest is the light ground when it is a page, the most colourful of the rest is the
 * colour it marks with and gives whole grounds to, and another colour that carries the light's words is a second dark.
 * It holds every contrast itself, so a theme is never darkened for white words (2026-09-29).
 */
export interface Theme {
  name: string;
  colours: [string, string] | [string, string, string];
}

const channels = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const toHex = (rgb: readonly number[]) => `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`;

/** One colour moved towards another, by a fraction. */
export function mixColour(a: string, b: string, t: number): string {
  const [x, y] = [channels(a), channels(b)];
  return toHex(x.map((v, i) => v + (y[i]! - v) * t));
}

/** Two colours a person would call the same colour. */
export function similarColours(a: string, b: string): boolean {
  const [x, y] = [channels(a), channels(b)];
  return x.reduce((sum, v, i) => sum + Math.abs(v - y[i]!), 0) < 90;
}

/** A colour somebody would call a colour: not a grey, not a near-white, not a near-black. */
export function isChromatic(hex: string): boolean {
  const c = channels(hex);
  const max = Math.max(...c), min = Math.min(...c);
  return max - min >= 40 && !(min > 200) && max > 50;
}

/** How far white words stand out on a colour, as WCAG measures it: 1 is invisible, 21 is black. */
export function contrastWithWhite(hex: string): number {
  const [r, g, b] = channels(hex).map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; }) as [number, number, number];
  return 1.05 / (0.2126 * r + 0.7152 * g + 0.0722 * b + 0.05);
}

/**
 * Themes for a business with no colours of its own yet: plain, and different from each other. None is Joogoos's own
 * ink and coral, which a customer's video never wears (ADR 0060, 2026-09-29).
 */
const STARTERS: readonly Theme[] = [
  { name: "Forest", colours: ["#1f4d3a", "#b7791f"] },
  { name: "Navy", colours: ["#1d3557", "#c8553d"] },
  { name: "Plum", colours: ["#4a2545", "#a86b32"] },
];

/**
 * A few colour themes to choose from (ADR 0029, 03 §your look), the owner's own first.
 *
 * - **Your colours**, as they are, when some are set: a choice already made is shown, never corrected.
 * - **From your logo** and **From your website**, when either gave colours: its main colour, and one that stands apart
 *   from it — another of its colours, or the dark ink it is drawn in. A logo with two colours and a dark ink keeps all
 *   three: a red mark with a blue swoosh and a slate handle lost its slate, the firm's own dark (2026-09-29).
 * - **Bold**: black with the business's main colour, which works for almost any brand.
 * - **Deep**: the main colour darkened, with a lighter shade of the same colour.
 *
 * Every colour is proposed as it is: the renderer puts dark words on a light colour and light words on a dark one, so
 * nothing needs darkening first. Darkening for white words turned a yellow into mustard before it was ever saved
 * (2026-09-29). Two themes that come out the same are one. A business with no colours at all gets a few plain starting
 * points instead.
 */
export function themeProposals(input: { current: readonly string[]; logo: readonly string[]; site: readonly string[] }): Theme[] {
  const out: Theme[] = [];
  const add = (name: string, a: string | undefined, b: string | undefined, keep = false, c?: string | undefined) => {
    if (!a) return;
    const two: [string, string] = [a, b ?? (keep ? a : mixColour(a, "#000000", 0.3))];
    const colours: Theme["colours"] = c ? [...two, c] : two;
    if (!out.some((t) => t.colours.join() === colours.join())) out.push({ name, colours });
  };
  if (input.current.length) add("Your colours", input.current[0], input.current[1], true, input.current[2]);
  // Proposed from real colours only: a pale tint or a grey is a page or a border, not a brand's colour.
  const logo = input.logo.filter(isChromatic);
  const site = input.site.filter(isChromatic);
  // The second colour is one that stands apart from the first: the next real colour of another family, or the dark ink
  // the logo or the site is drawn in. A second shade of the same green read as one colour (a customer's site, 2026-09-24).
  const partner = (main: string | undefined, ...from: ReadonlyArray<readonly string[]>) =>
    main ? from.flat().find((c) => !similarColours(c, main) && (isChromatic(c) || contrastWithWhite(c) >= 7)) : undefined;
  // A logo whose second is a colour of its own keeps the dark it is drawn in as a third, when it has one.
  const logoPartner = partner(logo[0], input.logo);
  const logoDark = logoPartner && isChromatic(logoPartner) ? input.logo.find((c) => !isChromatic(c) && contrastWithWhite(c) >= 7) : undefined;
  add("From your logo", logo[0], logoPartner, false, logoDark);
  add("From your website", site[0], partner(site[0], input.site) ?? logo[0]);
  const base = [...new Set([...logo, ...site])];
  const main = base[0];
  if (main) {
    add("Bold", "#000000", main);
    // One colour family: the main colour deepened, and a lighter shade of it. Mixing a logo's colour with an unrelated one
    // from the website read as two brands at once (2026-09-13: brown and purple).
    add("Deep", mixColour(main, "#000000", 0.45), mixColour(main, "#ffffff", 0.2));
  }
  if (!main) for (const t of STARTERS) add(t.name, t.colours[0], t.colours[1]);
  return out.slice(0, 5);
}

/**
 * How many colours a business keeps. The renderer reads them by role, not by position (`brandRoles`): the darkest, the
 * lightest, the most colourful of the rest, and a second dark that carries the light's words. A third that is none of
 * those is not drawn (ADR 0060).
 */
export const LOOK_COLOURS = 3;

const Hex = z.string().regex(/^#[0-9a-f]{6}$/i).transform((h) => h.toLowerCase());

/** `POST /v1/workspace/look`: the colours, or taking the logo off. Either may be left out. */
export const LookChange = z.object({
  palette: z.array(Hex).max(LOOK_COLOURS).optional(),
  logo: z.null().optional(),
});
export type LookChange = z.input<typeof LookChange>;

/** `POST /v1/workspace/brand`: the owner's choice. An empty choice is a real answer — "none of these". */
export const BrandChoice = z.object({
  keep: z.array(z.string().max(80)).max(80),
  logo: z.string().max(80).nullable(),
  colours: z.array(Hex).max(LOOK_COLOURS),
});
export type BrandChoice = z.infer<typeof BrandChoice>;

/** The candidate most likely to be the logo: the site's own icon or named logo before anything else. */
export function likelyLogo(candidates: readonly BrandCandidate[]): BrandCandidate | null {
  return candidates.find((c) => c.logo && c.source === "website") ?? candidates.find((c) => c.logo) ?? null;
}

export function brandView(kit: BrandKit | null | undefined): BrandView | null {
  if (!kit?.candidates.length) return null;
  const candidates = kit.candidates.map(({ key: _k, mime: _m, bytes: _b, sha256: _s, thumbKey: _t, ...view }) => view);
  const logo = likelyLogo(candidates);
  const colours = [...new Set([...(logo?.colours ?? []), ...kit.colours])].slice(0, 6);
  return { candidates, colours, siteColours: kit.colours, logo: logo?.id ?? null };
}

/** The one line under the finished step: what was kept, in words. */
export function keptDetail(pictures: number, logo: boolean, colours: number): string {
  const parts = [
    pictures ? `${pictures} ${pictures === 1 ? "picture" : "pictures"}` : "",
    logo ? "your logo" : "",
    colours ? `${colours} ${colours === 1 ? "colour" : "colours"}` : "",
  ].filter(Boolean);
  if (!parts.length) return "kept nothing — you can upload your own in the Studio";
  return `kept ${parts.length < 2 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`}`;
}
